import { CSSProperties, MouseEventHandler, ReactNode, forwardRef } from 'react';
import type { LucideIcon } from '@/shared/ui/icons';
import { ScrollEdgeBlur } from './scroll-edge-blur';
import { DrawerDescription, DrawerTitle } from './drawer';

/**
 * iOS-style sheet header used inside Drawer cards.
 *
 * Mirrors the layout of `UnifiedAppHeader`:
 *   • leading slot (left, glass pills) · centered title · trailing slot (right)
 *   • backed by `ScrollEdgeBlur` so content slides under it with the same
 *     feathered iOS edge-blur the main app header uses.
 *
 * The header itself is `pointer-events: none` so empty fade space passes
 * touches through. Interactive children (pills) re-enable themselves —
 * use the `<GlassPill>` helper for consistent styling.
 */
export interface DrawerCardHeaderProps {
  title: ReactNode;
  /** Hidden description for screen readers (Radix Dialog requires one). */
  description?: ReactNode;
  /** Buttons rendered on the left (typically a cancel/back glass pill). */
  leading?: ReactNode;
  /** Buttons rendered on the right (typically a save/confirm glass pill). */
  trailing?: ReactNode;
  /** Total band height in px. Default `64`. */
  height?: number;
  /**
   * How the header band positions itself. Default `static` — sibling above
   * a scroll container. Use `sticky` if rendering inside a scroll container
   * (rare; can interfere with Vaul touch handling). Use `absolute` to
   * overlay a sibling scroll container.
   */
  position?: 'static' | 'sticky' | 'absolute';
}

export function DrawerCardHeader({
  title,
  description,
  leading,
  trailing,
  height = 64,
  position = 'static',
}: DrawerCardHeaderProps) {
  return (
    <ScrollEdgeBlur height={height} position={position} style={{ zIndex: 40 }}>
      <div
        className="absolute top-0 left-0 right-0 flex flex-row items-start justify-between"
        style={{ height: 54, paddingLeft: 16, paddingRight: 20, paddingTop: 12 }}
      >
        <div className="flex items-center gap-2 min-w-[44px]">
          {leading}
        </div>
        <DrawerTitle asChild>
          <span
            className="absolute left-1/2 -translate-x-1/2 font-semibold font-apple whitespace-nowrap pointer-events-none select-none"
            style={{
              top: 12 + (44 - 22) / 2,
              fontSize: 17,
              lineHeight: '22px',
              letterSpacing: '-0.43px',
              color: 'var(--foreground)',
            }}
          >
            {title}
          </span>
        </DrawerTitle>
        <div className="flex items-center gap-2">
          {trailing}
        </div>
      </div>
      {description !== undefined && (
        <DrawerDescription className="sr-only">{description}</DrawerDescription>
      )}
    </ScrollEdgeBlur>
  );
}

/**
 * 44×44 glass-blur pill matching the app-header action buttons.
 * Use as `leading` or `trailing` content inside `DrawerCardHeader`.
 */
export interface GlassPillProps {
  /** Icon pill. Provide either `icon` or `label`. */
  icon?: LucideIcon;
  /** Text pill (e.g. "Done", "Cancel") — matches the app header's text pills. */
  label?: string;
  ariaLabel: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit';
  /** When `type="submit"`, target form id. */
  form?: string;
}

export const GlassPill = forwardRef<HTMLButtonElement, GlassPillProps>(
  ({ icon: Icon, label, ariaLabel, onClick, type = 'button', form }, ref) => {
    const surfaceStyle: CSSProperties = {
      position: 'absolute',
      inset: 0,
      borderRadius: 1000,
      background: 'var(--glass-surface)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      boxShadow: '0px 8px 24px 0px rgba(0,0,0,0.08), var(--glass-inset, none)',
    };

    return (
      <button
        ref={ref}
        type={type}
        form={form}
        onClick={onClick}
        aria-label={ariaLabel}
        className="relative flex items-center justify-center active:opacity-75 transition-opacity"
        style={{ height: 44, padding: '0 4px', borderRadius: 296, pointerEvents: 'auto' }}
      >
        <span aria-hidden="true" className="pointer-events-none" style={surfaceStyle} />
        <span
          className="relative flex items-center justify-center"
          style={{
            width: label ? 'auto' : 36,
            minWidth: 36,
            height: 36,
            paddingLeft: label ? 12 : 0,
            paddingRight: label ? 12 : 0,
          }}
        >
          {Icon ? (
            <Icon style={{ width: 17, height: 17, color: 'var(--foreground)' }} strokeWidth={2.25} />
          ) : (
            <span
              className="font-apple font-semibold whitespace-nowrap"
              style={{ color: 'var(--foreground)', fontSize: 15, lineHeight: '20px', letterSpacing: '-0.23px' }}
            >
              {label}
            </span>
          )}
        </span>
      </button>
    );
  },
);
GlassPill.displayName = 'GlassPill';
